import { Injectable, NgZone, inject } from "@angular/core";
import {
	type Cookie,
	type IndexDefinitions,
	type MutatorDefs,
	type ReadTransaction,
	Replicache,
	type SubscribeOptions,
} from "replicache";

import { Subject, takeUntil } from "rxjs";
import { Logger } from "src/app/services";
import { environment } from "src/environments/environment";
import {
	COOKIE,
	PokeEvent,
	PokeService,
	PullService,
	PushService,
	ReplicacheUtils,
	type Replicacheable,
	SyncState,
} from "./";

@Injectable({
	providedIn: "root",
})
export class ReplicacheService {
	private readonly _ngZone = inject(NgZone);
	private readonly _pullService = inject(PullService);
	private readonly _pokeService = inject(PokeService);
	private readonly _pushService = inject(PushService);

	private readonly _logger = inject(Logger);

	private _closed$ = new Subject<void>();

	private _replicache: Replicache<MutatorDefs> | undefined;

	public get replicache(): Replicache<MutatorDefs> {
		if (!this._replicache) throw new Error("Replicache not initialized.");
		return this._replicache;
	}

	private set replicache(replicache: Replicache<MutatorDefs>) {
		this._replicache = replicache;
	}

	public get initialized(): boolean {
		return !!this._replicache;
	}

	public get lastCookie(): Cookie {
		return COOKIE.get();
	}

	public indexDefinitions: IndexDefinitions = {};
	public mutatorDefinitions: MutatorDefs = {};

	public async init(models: Replicacheable[]): Promise<void> {
		this._logger.info("Initializing Replicache...");
		await this._ngZone.runOutsideAngular(async () => await this._init(models));
		await this._pokeService.connect();
	}

	public subscribe<T>(
		transaction: (tx: ReadTransaction) => Promise<T>,
		options: SubscribeOptions<T> | ((result: T) => void),
	): () => void {
		return this.replicache.subscribe<T>(transaction, options);
	}

	public mutate(mutation: string, args: unknown): Promise<unknown> {
		return this.replicache.mutate[mutation](args);
	}

	public forcePull(): Promise<void> {
		this._logger.log("FORCING PULL...");
		return this.replicache.pull({ now: true });
	}

	public async close(): Promise<void> {
		this._logger.info("Closing Replicache...");
		await this.replicache.close();
		await this._pokeService.disconnect();
		this._closed$.next();
		this._closed$.complete();
		this._closed$ = new Subject<void>();
	}

	private async _init(models: Replicacheable[]): Promise<void> {
		this.indexDefinitions = new ReplicacheUtils.IndexBuilder(models).build();
		this.mutatorDefinitions = new ReplicacheUtils.MutatorBuilder(
			models,
		).build();
		const { name, schemaVersion, licenseKey, experimental } =
			environment.replicache;
		const { pushEnabled } = experimental;
		const puller = this._pullService.puller;
		const pusher = this._pushService.pusher;
		const indexes = this.indexDefinitions;
		const mutators = this.mutatorDefinitions;
		this._replicache = new Replicache<MutatorDefs>({
			name,
			// TODO: Set pullInterval to null and implement a custom interval mechanism that is offline-aware
			pullInterval: 300000, // 5 minutes
			schemaVersion,
			licenseKey,
			puller,
			...(pushEnabled ? { pusher } : {}),
			indexes,
			mutators,
		});
		this._pullService.registerSyncStateObserver(this.replicache);
		this._registerPokeObserver();
	}

	private _registerPokeObserver(): void {
		const onPoke = async (event: PokeEvent) => {
			const { partitionKey } = event;
			if (this._pullService.syncState === SyncState.COMPLETE) {
				if (partitionKey) {
					this._logger.log(
						"POKED WITH PARTITION KEY:",
						partitionKey,
						"PULLING...",
					);
					this._pullService.pokePartitionKeyQueue.enqueue(partitionKey);
					await this.replicache.pull();
				} else {
					this._logger.log("POKED. PULLING...");
					await this.replicache.pull();
				}
			} else {
				this._logger.log("POKED. SYNC STATE IS NOT COMPLETE. NOT PULLING.");
			}
		};
		this._pokeService.onPoke$.pipe(takeUntil(this._closed$)).subscribe(onPoke);
	}
}
